/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DialogService, NotificationService } from '@gh/core-ui';
import { arrayify, getFileExtension } from '@gh/core-util';
import { flatMap } from 'lodash';

@Component({
  standalone: false,
  selector: 'gh-input-file',
  templateUrl: 'input-file.component.html',
  styleUrls: ['input-file.component.scss'],
})
export class InputFileComponent {
  @Input() disabled: boolean = false;
  @Input() accept: string = '';
  @Input() skin: string = '';
  @Input() title: string;
  @Input() errorInBalloon: boolean;
  @Input() flexCrossCenter: boolean;

  @Input() invalidTypeMessage: string = 'msg_import_prices_file_invalid';

  @Output() update: EventEmitter<any> = new EventEmitter();

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

  file: Maybe<File>;

  private _extensions: string[] = [];

  constructor(private notificationService: NotificationService,
              private dialogService: DialogService) {

  }

  @Input()
  set extensions(extensions: string | string[]) {
    this._extensions = flatMap(arrayify(extensions), (extension: string) => extension.split(/\s*,\s*/g))
      .map((ext) => ext.toLowerCase());
  }

  get fileName(): string {
    return this.file && this.file.name || '';
  }

  get isFileSelected(): boolean {
    return Boolean(this.file);
  }

  onSelect(file?: File): void {
    if (file && (this.isFileHasValidMimeType(file) || this.isFileHasValidExtension(file))) {
      this.file = file;
      this.update.emit(this.file);
    } else {
      if (file) {
        if (this.errorInBalloon) {
          this.notificationService.error(this.invalidTypeMessage);
        } else {
          this.dialogService.warning(this.invalidTypeMessage, { correctType: this.accept });
        }
      }
    }
    this.fileInput.nativeElement.value = null;
  }

  onFileInputClick(): void {
    if (this.skin === 'button') {
      return;
    }
    this.resetInputValueOnly();
  }

  onPickButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.resetInputValueOnly();
    this.fileInput.nativeElement.click();
  }

  private resetInputValueOnly(): void {
    this.fileInput.nativeElement.value = '';
  }

  clear(): void {
    this.file = void 0;
    this.fileInput.nativeElement.value = null;
  }

  private isFileHasValidMimeType(file: File): boolean {
    return !this.accept || file.type === this.accept;
  }

  private isFileHasValidExtension(file: File): boolean {
    const extension = getFileExtension(file.name).toLowerCase();
    return !this._extensions.length || this._extensions.includes(extension);
  }
}
